<script>
  /**
   * Tags of the entry being edited, as an island tool next to "Sichtbarkeit".
   *
   * The button shows the first tag (in its colour when it is a category). The popover lists the
   * entry's tags in their order, with "Nach vorne" to make one the first, and below a search
   * field over all tags: typing filters, Enter adds the match or creates a new tag. Tags are
   * saved with the entry, like every other entry field.
   */
  import { page } from '$app/state';
  import Popover from '#lib/Popover.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import CategoryLabel from '#lib/CategoryLabel.svelte';
  import t from '#lib/i18n.svelte.js';
  import { fetch_json, generate_css_variables } from '#lib/util.js';
  import { get_entry_category } from '#lib/entry_category.js';

  let { entry } = $props();

  let anchor_ref = $state();
  let all_tags = $state([]);
  let search_term = $state('');
  let loaded = $state(false);

  let tag_keys = $derived(entry.tag_keys ?? []);
  let colors = $derived(Object.fromEntries(all_tags.filter(tag => tag.color_id).map(tag => [tag.tag_key, tag.color_id])));
  let tag_colors = $derived({ ...(page.data.entry_tag_colors ?? {}), ...colors });
  let category = $derived(get_entry_category(tag_keys, tag_colors));

  let query = $derived(search_term.trim().toLowerCase());
  let other_tags = $derived(
    all_tags
      .filter(tag => !tag_keys.includes(tag.tag_key))
      .filter(tag => !query || tag.tag_key.toLowerCase().includes(query))
  );
  let can_create = $derived(
    query.length > 0 &&
    !query.includes(',') &&
    !all_tags.some(tag => tag.tag_key.toLowerCase() === query) &&
    !tag_keys.some(key => key.toLowerCase() === query)
  );

  async function load_tags() {
    try {
      all_tags = await fetch_json('GET', '/api/get-all-entry-tags');
      loaded = true;
    } catch (error) {
      console.error('Failed to load entry tags:', error);
    }
  }

  function set_tags(keys) {
    entry.tag_keys = keys;
  }

  function add(key) {
    if (!key || tag_keys.includes(key)) return;
    set_tags([...tag_keys, key]);
    search_term = '';
  }

  function remove(key) {
    set_tags(tag_keys.filter(k => k !== key));
  }

  function move_to_front(key) {
    set_tags([key, ...tag_keys.filter(k => k !== key)]);
  }

  async function create(key) {
    try {
      await fetch_json('POST', '/api/create-entry-tag', { tag_key: key });
    } catch (error) {
      // assign anyway: the tag is created when the entry is saved
      console.error('Failed to create tag:', error);
    }
    add(key);
    await load_tags();
  }

  function on_search_keydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const exact = all_tags.find(tag => tag.tag_key.toLowerCase() === query);
    if (exact && !tag_keys.includes(exact.tag_key)) add(exact.tag_key);
    else if (can_create) create(search_term.trim());
    else if (other_tags.length === 1) add(other_tags[0].tag_key);
  }

  function tag_count(key) {
    return all_tags.find(tag => tag.tag_key === key)?.entry_count ?? 0;
  }
</script>

{#if page.data.current_user?.role === 'admin'}
  <button
    bind:this={anchor_ref}
    popovertarget='tags_tool_{entry.entry_id ?? 'new'}'
    onclick={() => { if (!loaded) load_tags(); }}
    title={tag_keys.length ? `${t('tags')}: ${tag_keys.join(', ')}` : t('tags')}
  >
    <ButtonLabel variant='brand ghostly text-compact'>
      <!-- Fixed width: the label stays "Tags"; the category's mark and the count say what is set -->
      <span class='tags-trigger'>
        {#if category}
          <i class='mark' style={generate_css_variables(category.color_id)} aria-hidden='true'></i>
        {/if}
        <span>{t('tags')}</span>
        {#if tag_keys.length > 0}
          <span class='more'>{tag_keys.length}</span>
        {/if}
      </span>
    </ButtonLabel>
  </button>

  <Popover
    anchor={anchor_ref}
    id='tags_tool_{entry.entry_id ?? 'new'}'
    placement='bottom-start'
    class='normal'
  >
    <div class='tags-tool text-control'>
      <section class='assigned'>
        <h3 class='heading text-caption'>{t('tags_of_entry')}</h3>
        {#if tag_keys.length === 0}
          <p class='empty text-caption'>{t('no_tags_yet')}</p>
        {:else}
          <ul>
            {#each tag_keys as key, index (key)}
              <li>
                {#if tag_colors[key]}
                  <CategoryLabel category={{ tag_key: key, color_id: tag_colors[key] }} text_style='text-control' />
                {:else}
                  <span class='plain-tag'><i class='mark empty' aria-hidden='true'></i>{key}</span>
                {/if}
                <span class='row-actions'>
                  {#if index > 0}
                    <button class='text-action' onclick={() => move_to_front(key)}>{t('move_to_front')}</button>
                  {/if}
                  <button class='text-action' onclick={() => remove(key)}>{t('remove')}</button>
                </span>
              </li>
            {/each}
          </ul>
          <p class='note text-caption'>{t('first_colored_tag_note')}</p>
        {/if}
      </section>

      <section class='available'>
        <input
          class='search text-control'
          type='search'
          placeholder={t('search_or_create_tag')}
          aria-label={t('search_or_create_tag')}
          bind:value={search_term}
          onkeydown={on_search_keydown}
        />
        <ul>
          {#each other_tags as tag (tag.tag_id)}
            <li>
              <button class='add' onclick={() => add(tag.tag_key)}>
                {#if tag.color_id}
                  <CategoryLabel category={{ tag_key: tag.tag_key, color_id: tag.color_id }} text_style='text-control' />
                {:else}
                  <span class='plain-tag'><i class='mark empty' aria-hidden='true'></i>{tag.tag_key}</span>
                {/if}
                <span class='count text-caption'>{tag_count(tag.tag_key)}</span>
              </button>
            </li>
          {/each}
          {#if can_create}
            <li>
              <button class='add' onclick={() => create(search_term.trim())}>
                <span class='plain-tag'>{t('create_new_tag')}: „{search_term.trim()}“</span>
              </button>
            </li>
          {/if}
        </ul>
      </section>

      <a class='manage text-control' href='/settings#tags'>{t('manage_tags_and_colors')}</a>
    </div>
  </Popover>
{/if}

<style>
  .tags-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--size-3);
    white-space: nowrap;
    .more {
      color: var(--text-2);
    }
  }
  .mark {
    flex: none;
    width: 0.62em;
    height: 0.62em;
    border-radius: var(--mark-radius);
    background: var(--color-emphasis-background);
    &.empty {
      background: transparent;
      box-shadow: inset 0 0 0 1px var(--color-default-stroke);
    }
  }
  .tags-tool {
    width: min(22rem, calc(100vw - 2 * var(--size-4)));
    display: flex;
    flex-direction: column;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
  section {
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    padding: var(--size-4);
    & + section {
      border-block-start: 1px solid var(--color-default-stroke);
    }
  }
  .heading {
    margin: 0;
    color: var(--text-2);
  }
  .empty, .note {
    margin: 0;
    color: var(--text-2);
  }
  .assigned li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--size-4);
    min-height: calc(var(--input-min-height) * 0.8);
  }
  .row-actions {
    display: flex;
    gap: var(--size-4);
    flex: none;
  }
  .text-action {
    color: var(--text-2);
    @media (hover: hover) {
      &:hover {
        color: var(--text-1);
        text-decoration: underline;
      }
    }
  }
  .plain-tag {
    display: inline-flex;
    align-items: center;
    gap: var(--size-3);
  }
  .search {
    min-height: var(--input-min-height);
    padding-inline: var(--size-4);
    border: 1px solid var(--color-default-stroke);
    border-radius: var(--input-border-radius);
    background: var(--color-background);
    color: inherit;
    margin-block-end: var(--size-2);
  }
  .available ul {
    max-height: 16rem;
    overflow-y: auto;
  }
  .add {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: var(--size-4);
    min-height: calc(var(--input-min-height) * 0.8);
    text-align: start;
    @media (hover: hover) {
      &:hover {
        background: var(--color-default-background-50);
      }
    }
  }
  .count {
    color: var(--text-3);
    font-variant-numeric: tabular-nums;
  }
  .manage {
    padding: var(--size-4);
    border-block-start: 1px solid var(--color-default-stroke);
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }
</style>
